import shakaUtils from './shakaUtils';
import ShakaLib from '../../all/shakaPlayer/ShakaLib';
import logger from 'src/utils/logger/logger';

class shakaPlayer {
  constructor() {
    /** @private {shaka.cast.CastProxy} */
    this.castProxy_ = null;

    /** @private {HTMLMediaElement} */
    this.video_ = null;

    /** @private {shaka.Player} */
    this.player_ = null;

    /** @private {shaka.Player} */
    this.localPlayer_ = null;

    /** @private {shakaExtern.SupportType} */
    this.support_;

    /** @private {ShakaControls} */
    this.controls_ = null;
  }

  /**
   * GET && SET
   * */

  static getVideo() {
    return this.video_;
  }

  static setVideo(video) {
    this.video_ = video;
  }

  static getPlayer() {
    return this.player_;
  }

  static setPlayer(player) {
    this.player_ = player;
  }

  /**
   * Prepares the Player to load the given assets by setting the configuration
   * values.  This does not load the asset.
   *
   * @param {?shakaAssets.AssetInfo} asset
   * @return {shakaAssets.AssetInfo}
   * @private
   */
  static preparePlayer(asset, failureCallback) {
    let configFromMetadata = shakaUtils.getShakaConfiguration();
    const shaka = ShakaLib.getShakaObject();

    const config = /** @type {shakaExtern.PlayerConfiguration} */ ({
      abr: {},
      manifest: { dash: {} }
    });
    config.manifest.dash.clockSyncUri = asset.manifestUri;
    config.streaming = config.streaming || {};
    config.drm = config.drm || {};

    /**
     * Manifest config
     */
    if (
      configFromMetadata &&
      configFromMetadata.manifest &&
      configFromMetadata.manifest.retryParameters
    ) {
      config.manifest.retryParameters = configFromMetadata.manifest.retryParameters;
    } else {
      config.manifest.retryParameters = {
        timeout: 10000, // timeout in ms, after which we abort a request; 0 means never
        maxAttempts: 2, // the maximum number of requests before we fail
        baseDelay: 1000, // the base delay in ms between retries
        backoffFactor: 2, // the multiplicative backoff factor between retries
        fuzzFactor: 0.7 // the fuzz factor to apply to each retry delay
      };
    }
    /**
     * DRM config
     */
    if (
      configFromMetadata &&
      configFromMetadata.drm &&
      configFromMetadata.drm.retryParameters
    ) {
      config.drm.retryParameters = configFromMetadata.drm.retryParameters;
    } else {
      config.drm.retryParameters = {
        timeout: 10000, // timeout in ms, after which we abort a request; 0 means never
        maxAttempts: 3, // the maximum number of requests before we fail
        baseDelay: 1000, // the base delay in ms between retries
        backoffFactor: 2, // the multiplicative backoff factor between retries
        fuzzFactor: 0.7 // the fuzz factor to apply to each retry delay
      };
    }

    /**
     * Streaming/segments config
     */
    if (
      configFromMetadata &&
      configFromMetadata.streaming &&
      configFromMetadata.streaming.retryParameters
    ) {
      config.streaming.retryParameters = configFromMetadata.streaming.retryParameters;
    } else {
      config.streaming.retryParameters = {
        timeout: 10000, // timeout in ms, after which we abort a request; 0 means never
        maxAttempts: 7, // the maximum number of requests before we fail
        baseDelay: 1000, // the base delay in ms between retries
        backoffFactor: 2, // the multiplicative backoff factor between retries
        fuzzFactor: 0.5 // the fuzz factor to apply to each retry delay
      };
    }

    config.streaming.bufferingGoal =
      configFromMetadata &&
      configFromMetadata.streaming &&
      configFromMetadata.streaming.bufferingGoal
        ? configFromMetadata.streaming.bufferingGoal
        : 15; // Segundos
    config.streaming.rebufferingGoal =
      configFromMetadata &&
      configFromMetadata.streaming &&
      configFromMetadata.streaming.rebufferingGoal
        ? configFromMetadata.streaming.rebufferingGoal
        : 2; // segs
    config.streaming.bufferBehind =
      configFromMetadata &&
      configFromMetadata.streaming &&
      configFromMetadata.streaming.bufferBehind
        ? configFromMetadata.streaming.bufferBehind
        : 30; // segs
    config.streaming.smallGapLimit =
      configFromMetadata &&
      configFromMetadata.streaming &&
      configFromMetadata.streaming.smallGapLimit
        ? configFromMetadata.streaming.smallGapLimit
        : 0.5; // The limit (in seconds) for a gap in the media to be considered "small". Small gaps are jumped automatically without events. Large gaps result in a Player event and can be jumped.
    config.streaming.jumpLargeGaps =
      configFromMetadata &&
      configFromMetadata.streaming &&
      configFromMetadata.streaming.jumpLargeGaps
        ? configFromMetadata.streaming.jumpLargeGaps
        : false; //If true, jump large gaps in addition to small gaps. A largegap event will be raised first. Then, if the app doesn't call preventDefault() on the event, the Player will jump the gap. If false, then the event will be raised, but the gap will not be jumped.

    /*
    Dont use this below unless you know what you do....only for test at this moment (ask if it is good to configure, I dont know at this moment).
    config.streaming.stallEnabled = true; // When set to true, the stall detector logic will run, skipping forward stallSkip seconds whenever the playhead stops moving for stallThreshold seconds.
    config.streaming.stallThreshold = 4; // The maximum number of seconds that may elapse without the playhead moving (when playback is expected) before it will be labeled as a stall.
    config.streaming.stallSkip = 2; // The number of seconds that the player will skip forward when a stall has been detected.
    */

    config.streaming.failureCallback = function(err) {
      // If no critical, let player to recover automagically itsef
      if (err.severity === shaka.util.Error.Severity.RECOVERABLE) {
        logger.debug('[********************************] PLAYER retryStreaming');
        this.player_.retryStreaming();
      } else if (err.severity === shaka.util.Error.Severity.CRITICAL) {
        /**
         * From shaka docs: if critical error, timeout errors cause shaka player to stop, so handle this specific error:
         * Severity.CRITICAL => A critical error that the library cannot recover from.
         *                      These usually cause the Player to stop loading or updating.
         *                      A new manifest must be loaded to reset the library.
         * Code.TIMEOUT      => A network request timed out.
         */
        // show modal to retry playing
        if (failureCallback) {
          // Handle this particular error (video freeze)
          failureCallback(err);
        }
      } else {
        this.player_.retryStreaming();
      }
    };

    this.player_.resetConfiguration();

    // Add config from this asset.
    shakaUtils.setupAssetMetadata(asset, this.player_);

    config.preferredAudioLanguage = 'es-419';
    config.preferredTextLanguage = 'es-419';
    this.player_.configure(config);

    return asset;
  }
}

export default shakaPlayer;



// WEBPACK FOOTER //
// ./src/devices/all/shakaPlayer/shakaMain.js